import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useCurrency } from '../context/CurrencyContext';
import { ordersService } from '../services/api';
import Modal from '../components/Common/Modal';
import { getSizeLabel } from '../utils/sizeOptions';

const CartPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const { cart, removeFromCart, clearCart, getCartTotal, getCartItemCount, updateCartItemQuantity } = useCart();
  const { formatPrice } = useCurrency();
  
  // Modal state
  const [modal, setModal] = useState({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
    onConfirm: null
  });

  // Loading state for checkout
  const [isCheckingOut, setIsCheckingOut] = useState(false);

  const showModal = (type, title, message, onConfirm = null) => {
    setModal({
      isOpen: true,
      type,
      title,
      message,
      onConfirm
    });
  };

  const closeModal = () => {
    setModal(prev => ({ ...prev, isOpen: false }));
  };

  // Format cart items for API
  const formatCartItemsForAPI = () => {
    return cart.map(item => {
      const basePrice = parseFloat(item.drink?.price) || 0;
      const toppingsPrice = item.toppings ? 
        item.toppings.reduce((sum, topping) => sum + (parseFloat(topping.price) || 0), 0) : 0;
      
      return {
        menuItemId: item.drink?.id || 'unknown',
        menuItemName: item.drink?.name || 'Unknown Item',
        quantity: item.quantity || 1,
        size: item.size || 'large',
        itemPrice: basePrice,
        toppings: item.toppings ? item.toppings.map(t => ({
          id: t.id,
          name: t.name,
          price: parseFloat(t.price) || 0
        })) : [],
        toppingsPrice: toppingsPrice,
        totalItemPrice: item.total || ((basePrice + toppingsPrice) * (item.quantity || 1)),
        customizations: {},
        notes: item.notes || ''
      };
    });
  };

  if (!isAuthenticated) {
    return (
      <div className="cart-page">
        <div className="container">
          <div className="auth-required">
            <h2>Login Required</h2>
            <p>Please login to view your cart</p>
            <button onClick={() => navigate('/auth')} className="btn btn-primary">
              Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  const handleCheckout = async () => {
    if (isCheckingOut) return; // Prevent multiple clicks
    setIsCheckingOut(true);

    try {
      // Tax-inclusive calculation (menu prices already include 5% GST)
      const subtotalTaxInclusive = getCartTotal(); // This is the tax-inclusive total
      
      // Calculate base price and tax breakdown for tax-inclusive system
      const gstPercentage = 5.0; // 5% GST
      const gstRate = gstPercentage / 100;
      const baseAmount = subtotalTaxInclusive / (1 + gstRate); // Base price before tax
      const gstAmount = subtotalTaxInclusive - baseAmount; // Tax amount included
      
      console.log('[CartPage] Tax-inclusive calculation:', {
        subtotalTaxInclusive,
        baseAmount,
        gstAmount,
        gstPercentage
      });

      // Prepare order data according to backend API structure
      const orderData = {
        storeId: '67d24584bcf9e988caa48986', // Main website store ID - TODO: Make this configurable
        orderType: 'in-app', // Order type for website orders
        paymentMethod: 'pending', // Payment pending since no gateway integrated
        customerName: user?.name || 'Website Customer',
        customerMobile: user?.mobile || user?.phone || null,
        customerId: user?.id || user?.mobile || null,
        
        // Tax-inclusive fields (matching SalesCapture.js format)
        subtotal: subtotalTaxInclusive, // Tax-inclusive subtotal
        baseSubtotal: baseAmount, // Base price before taxes
        subtotalGstAmount: gstAmount, // GST amount included in subtotal
        subtotalTaxAmount: 0, // No additional tax
        subtotalTotalTaxAmount: gstAmount, // Total tax amount included
        
        discountAmount: 0,
        deliveryCharges: 0,
        
        // Final amounts (same as subtotal for orders with no discount/delivery)
        finalBaseAmount: baseAmount,
        gstAmount: gstAmount,
        taxAmount: 0,
        totalTaxAmount: gstAmount,
        totalAmount: subtotalTaxInclusive, // Final total (tax-inclusive)
        
        // Tax configuration for audit trail
        taxSettings: {
          gstPercentage: gstPercentage,
          taxPercentage: 0.0,
          enableGST: true,
          enableTax: false,
          taxMethod: 'inclusive',
          capturedAt: new Date().toISOString(),
          version: '2.0' // Version to track tax calculation method
        },
        
        items: formatCartItemsForAPI(),
        notes: 'Website order - Payment pending. Customer should visit counter.',
        address: null // No delivery for now
      };

      console.log('[CartPage] Creating order with tax-inclusive data:', orderData);

      const response = await ordersService.createOrder(orderData);
      
      if (response.data && response.data.success) {
        const orderInfo = response.data.data;
        const orderNumber = orderInfo.dailyOrderNumber || orderInfo.id;
        
        // Clear cart immediately after successful order
        clearCart();
        
        // Redirect to orders page immediately with success state
        navigate('/orders', { 
          state: { 
            fromCheckout: true, 
            orderNumber,
            justPlaced: true 
          } 
        });
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error) {
      console.error('[CartPage] Error placing order:', error);
      
      let errorMessage = 'There was an error placing your order. Please try again later.';
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      showModal(
        'error',
        'Order Failed',
        errorMessage
      );
    } finally {
      setIsCheckingOut(false);
    }
  };

  const handleRemoveItem = (itemId, itemName) => {
    showModal(
      'confirm',
      'Remove Item',
      `Are you sure you want to remove "${itemName}" from your cart?`,
      () => removeFromCart(itemId)
    );
  };

  const handleClearCart = () => {
    showModal(
      'confirm',
      'Clear Cart',
      'Are you sure you want to remove all items from your cart?',
      () => clearCart()
    );
  };

  if (cart.length === 0) {
    return (
      <div className="cart-page">
        <div className="container">
          <div className="empty-cart">
            <h2>Your cart is empty</h2>
            <p>Add some delicious drinks to get started!</p>
            <button onClick={() => navigate('/menu')} className="btn btn-primary">
              Browse Menu
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="cart-page">
      <div className="container">
        <div className="cart-header">
          <h1>Your Cart ({getCartItemCount()} items)</h1>
          <button onClick={handleClearCart} className="btn btn-secondary">
            Clear Cart
          </button>
        </div>

        <div className="cart-items">
          {cart.map(item => (
            <div key={item.id} className="cart-item">
              <div className="item-info">
                <h3>{item.drink.name}</h3>
                <p>Size: {getSizeLabel(item.size)}</p>
                {item.toppings && item.toppings.length > 0 && (
                  <p>Toppings: {item.toppings.map(t => t.name).join(', ')}</p>
                )}
                <p>Quantity: {item.quantity}</p>
              </div>
              <div className="item-price">
                <span>{formatPrice(item.total)}</span>
                <button 
                  onClick={() => handleRemoveItem(item.id, item.drink.name)}
                  className="remove-btn"
                >
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="cart-summary">
          <div className="total">
            <h2>Total: {formatPrice(getCartTotal())}</h2>
          </div>
          <button onClick={handleCheckout} className="btn btn-primary btn-large" disabled={isCheckingOut}>
            {isCheckingOut ? 'Placing Order...' : 'Checkout'}
          </button>
        </div>
      </div>

      <Modal
        isOpen={modal.isOpen}
        onClose={closeModal}
        type={modal.type}
        title={modal.title}
        message={modal.message}
        onConfirm={modal.onConfirm}
      />

      <style jsx>{`
        .cart-page {
          min-height: 100vh;
          background-color: #fff8f0;
          padding: 40px 0 80px;
        }
        
        .auth-required,
        .empty-cart {
          text-align: center;
          padding: 100px 20px;
        }
        
        .cart-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 30px;
        }
        
        .cart-header h1 {
          color: #ff9dd4;
        }
        
        .cart-items {
          margin-bottom: 30px;
        }
        
        .cart-item {
          background-color: white;
          border-radius: 10px;
          padding: 20px;
          margin-bottom: 15px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
        }
        
        .item-info h3 {
          color: #ff9dd4;
          margin-bottom: 10px;
        }
        
        .item-info p {
          color: #666;
          margin-bottom: 5px;
        }
        
        .item-price {
          text-align: right;
        }
        
        .item-price span {
          font-size: 1.2rem;
          font-weight: bold;
          color: #ff9dd4;
          display: block;
          margin-bottom: 10px;
        }
        
        .remove-btn {
          background-color: #ef4444;
          color: white;
          border: none;
          padding: 5px 10px;
          border-radius: 5px;
          cursor: pointer;
        }
        
        .cart-summary {
          background-color: white;
          border-radius: 15px;
          padding: 30px;
          text-align: center;
          box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
        }
        
        .total h2 {
          color: #ff9dd4;
          margin-bottom: 20px;
        }
        
        .btn-large {
          padding: 15px 40px;
          font-size: 1.1rem;
          width: 100%;
        }
      `}</style>
    </div>
  );
};

export default CartPage; 